import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

/**
 * PlayerCoverZoom — Cover thumbnail + full-screen zoom overlay for the audio player.
 *
 * Sized against the player's viewport (dvw/dvh) rather than a fixed multiplier,
 * because the player is used almost entirely on mobile where a literal 3× thumb
 * would exceed the screen. Uses dynamic viewport units so mobile browser
 * chrome (address bar showing/hiding) doesn't shift the sizing.
 *
 * Playback is NOT paused when the overlay opens — the <audio> element in the
 * parent stays mounted; this component only renders an on-top layer.
 */
const PlayerCoverZoom = ({ coverUrl, altText, fallbackUrl = '/newaudia-logo.jpg' }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const triggerRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!isZoomed) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setIsZoomed(false);
      }
    };
    // Focus the close button when zoom opens (basic focus mgmt)
    closeBtnRef.current?.focus();
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      // Return focus to the trigger when zoom closes
      triggerRef.current?.focus();
    };
  }, [isZoomed]);

  return (
    <>
      {/* Thumbnail with magnifying-glass affordance */}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-label="Zoom book cover"
        onClick={() => setIsZoomed(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsZoomed(true);
          }
        }}
        className="relative inline-flex items-center justify-center h-full max-w-full cursor-zoom-in group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-lg"
        data-testid="player-cover-zoom-trigger"
      >
        <img
          src={coverUrl}
          alt={altText}
          onError={(e) => {
            e.currentTarget.src = fallbackUrl;
          }}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl pointer-events-none"
        />
        <span
          aria-hidden="true"
          className="absolute bottom-1.5 right-1.5 flex items-center justify-center w-7 h-7 rounded-full bg-black/60 text-white shadow-md opacity-90 group-hover:opacity-100 transition-opacity pointer-events-none"
        >
          <Search className="w-4 h-4" strokeWidth={2.5} />
        </span>
      </div>

      {/* Full-screen zoom overlay — sits above player chrome (z-[60]) */}
      {isZoomed && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 animate-in fade-in"
          onClick={() => setIsZoomed(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`Zoomed cover for ${altText}`}
          data-testid="player-cover-zoom-overlay"
        >
          <button
            ref={closeBtnRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsZoomed(false);
            }}
            aria-label="Close zoomed cover"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center justify-center w-10 h-10 rounded-full bg-black/50 text-white hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors"
            data-testid="player-cover-zoom-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <img
            src={coverUrl}
            alt={altText}
            onError={(e) => {
              e.currentTarget.src = fallbackUrl;
            }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[66dvw] max-h-[66dvh] object-contain rounded-lg shadow-2xl cursor-default select-none"
            data-testid="player-cover-zoom-image"
          />
        </div>
      )}
    </>
  );
};

export default PlayerCoverZoom;
